/**
 * ParticipantReview.jsx
 * Google Reviews-style review card.
 *
 * Design features:
 *  - White card with Google-brand shadow
 *  - Google multicolor "G" logo (SVG) in top-right corner
 *  - Coloured avatar circle with reviewer initial (Google palette: blue/red/yellow/green)
 *  - Reviewer name + "Completed the Program" subtitle
 *  - 5-star rating in Google yellow (#FBBC04), filled vs outline
 *  - Review text with overflow scroll on long quotes
 *  - Clickable card links to the Google review if a link is provided
 */

/* ── Avatar colour palette (matches Google's reviewer avatar colours) ── */
const AVATAR_COLORS = ['#4285F4', '#EA4335', '#34A853', '#FBBC04', '#FF6D00', '#9C27B0'];

/**
 * Picks a consistent colour for a reviewer based on their name's first character.
 * @param {string} name
 * @returns {string} hex colour
 */
function avatarColor(name = '') {
  const code = (name.charCodeAt(0) || 0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[code];
}

/** Google multicolor G logo as an inline SVG */
const GoogleLogo = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="20"
    height="20"
    aria-label="Google review"
  >
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

/** Renders N filled stars + (5-N) outlined stars in Google yellow */
const StarRating = ({ rating = 5 }) => {
  const filled = Math.max(0, Math.min(5, Math.round(rating)));
  return (
    <div className="review-stars" aria-label={`${filled} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => (
        <span key={i} className={i < filled ? 'star filled' : 'star empty'}>
          {i < filled ? '★' : '☆'}
        </span>
      ))}
    </div>
  );
};

const ParticipantReview = ({ review }) => {
  const initial = (review.name || '?').charAt(0).toUpperCase();
  const bgColor = avatarColor(review.name);

  const CardWrapper = ({ children }) =>
    review.link ? (
      <a
        href={review.link}
        target="_blank"
        rel="noreferrer"
        className="review-card"
        aria-label={`Read ${review.name}'s full Google review`}
      >
        {children}
      </a>
    ) : (
      <article className="review-card">{children}</article>
    );

  return (
    <CardWrapper>
      {/* ── Top row: avatar + name/subtitle + Google logo ── */}
      <div className="review-header">
        <div className="review-avatar" style={{ background: bgColor }}>
          {initial}
        </div>
        <div className="review-meta">
          <span className="review-name">{review.name}</span>
          <span className="review-sub">Completed the Program</span>
        </div>
        <div className="review-google-logo">
          <GoogleLogo />
        </div>
      </div>

      {/* ── Star rating ── */}
      <StarRating rating={review.rating ?? 5} />

      {/* ── Review text ── */}
      <p className="review-quote">"{review.quote}"</p>
    </CardWrapper>
  );
};

export default ParticipantReview;
